import { EmbeddableFlowDataQualityWarning } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';

import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/ts/models/IDataQualityIndicatorInfo';

import { CERTIFIED_INDICATOR_TYPE, dataQualityIndicatorInfosToProps } from '../../app/quality/DataQualityType';
import { SiteUrlsProps } from '../../app/selectors/SiteUrlsSelectors';
import { EditFlowEntityAction } from '../../app/spaces/ActionsMenu/EntityActions/EditFlowEntityAction';
import { ConnectedExtractMetadata } from '../../app/spaces/Header/ExtractMetadata';
import { ModifiedDateMetadata, OwnerMetadata } from '../../app/spaces/Header/HeaderMetadataRow';
import { ActionsMenuIcon, FavoriteToggleWithCountsIcon } from '../../app/spaces/Header/HeaderNameRowIcons';
import { ConnectedSpaceHeader } from '../../app/spaces/Header/SpaceHeader';
import { SpaceHeaderStyles } from '../../app/spaces/Header/SpaceHeaderStyles';
import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ISpaceItemDetailInfo } from '../../ts/models/ISpaceItemDetailInfo';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { DataCatalogUIActionsService } from '../../ts/services/DataCatalogUIActionsService';
import { FlowDraftBanner } from './FlowDraftBanner';

export interface FlowSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'flow'>;
  readonly availableActions: FlowAllowedActions;
  readonly catalogingOnSiteEnabled: boolean;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}

export function FlowSpaceHeader(props: FlowSpaceHeaderProps): JSX.Element {
  const shouldShowDraftBanner = FeatureFlags.flowDraftsEnabled && props.item && props.item.flowDraft;
  return <ConnectedSpaceHeader
    item={props.item}
    availableActions={props.availableActions}
    nameRowIcons={
      <React.Fragment>
        {FavoriteToggleWithCountsIcon({
          item: props.item,
          addToFavorites: props.addToFavorites,
          removeFromFavorites: props.removeFromFavorites,
        })}
        {ActionsMenuIcon(props.item)}
      </React.Fragment>
    }
    metadataRow={
      <React.Fragment>
        {OwnerMetadata(props.item.owner, props.urls)}
        {ModifiedDateMetadata(props.item.updatedAt)}
        <ConnectedExtractMetadata itemInfo={props.item} availableActions={props.availableActions} />
        {selectCatalogingOnSiteEnabled &&
          <DataQualityMetadata flowInfo={props.item} urls={props.urls} />}
      </React.Fragment>
    }
    spotlightActions={shouldShowDraftBanner ? [] : [EditFlowEntityAction]}
    bottomElement={shouldShowDraftBanner ? <FlowDraftBanner flow={props.item} /> : null}
  />;
}

function DataQualityMetadata(props: {
  flowInfo: ISpaceItemDetailInfo<'flow'>;
  urls: SiteUrls;
}): JSX.Element | null {

  const anchoredWarnings = props.flowInfo.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE);
  const dataQualityWarningsOnThisItem = dataQualityIndicatorInfosToProps(
    {
      assetUrl: props.urls.flowUrl(props.flowInfo.id),
      assetName: props.flowInfo.name,
      assetType: 'Flow',
      onContentItem: true
    },
    props.flowInfo.dataQualityIndicators
  );

  return (
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      <EmbeddableFlowDataQualityWarning
        client={catalogApolloClient}
        flowVizportalUrlId={props.flowInfo.id}
        ownContentWarnings={dataQualityWarningsOnThisItem}
        siteUrls={props.urls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        featureFlags={catalogFeatureFlagsAdapter()}
        asset={{
          assetName: props.flowInfo.name,
          assetType: 'Flow',
          assetUrl: urls => urls.flowUrl(props.flowInfo.id)
        }}
        anchoredWarnings={anchoredWarnings.map(i => dataQualityIndicatorInfoToDataQualityIndicator(i))}
      />
    </span>
  );
}
